
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

export const MODAL_SIZE_MAP: Record<ModalSize, string> = {
  sm: "420px",
  md: "560px",
  lg: "780px",
  xl: "1040px",
  full: "calc(100vw - 32px)",
};

export interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Vordefinierte Breite. `maxWidth` überschreibt dies. */
  size?: ModalSize;
  /** Escape-Hatch: expliziter CSS max-width (z.B. "56rem", "900px"). Überschreibt `size`. */
  maxWidth?: string;
  /** Tailwind max-w-* Klasse auf dem Dialog (Alternative zu size/maxWidth, überschreibt beide). */
  maxWidthClass?: string;
  /** CSS max-height des Dialogs (Default: Viewport minus Backdrop-Padding). */
  maxHeight?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  /** Klassen auf dem Dialog-Container. */
  className?: string;
  ariaLabel?: string;
}

/**
 * Stack offener Modals — nur das oberste reagiert auf Escape / Backdrop-Click,
 * damit das Schließen eines verschachtelten Modals nicht die darunterliegenden
 * mitschließt. Modulweit geteilt über alle BaseModal-Instanzen.
 */
let modalStack: symbol[] = [];

/**
 * Rohe Modal-Shell: Portal → body, Backdrop + Zentrierung, Body-Scroll-Lock,
 * Escape/Backdrop-Close (stacking-aware), Größe. KEIN Header/Footer — `children`
 * füllen den Dialog frei. Für die „kann alles"-Variante mit Header/Footer siehe
 * {@link Modal}.
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  children,
  size = "md",
  maxWidth,
  maxWidthClass,
  // dvh statt vh: auf Telefonen ist 100vh die Höhe OHNE eingeblendete
  // URL-Leiste — der Dialog ragte dadurch unten aus dem Bild.
  maxHeight = "calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = "",
  ariaLabel,
}) => {
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol("modal");

  const isTopmost = useCallback(() => modalStack[modalStack.length - 1] === idRef.current, []);

  // Stack-Registrierung + Body-Scroll-Lock (nur an open gekoppelt).
  useEffect(() => {
    if (!open) return;
    const id = idRef.current!;
    modalStack.push(id);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      modalStack = modalStack.filter((m) => m !== id);
      if (modalStack.length === 0) document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTopmost()) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, closeOnEscape, onClose, isTopmost]);

  const onBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!closeOnBackdrop || !isTopmost()) return;
      if (e.target === e.currentTarget) onClose();
    },
    [closeOnBackdrop, onClose, isTopmost]
  );

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="ds-modal-backdrop"
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        // Safe-Area: sonst liegt der Dialogrand unter Notch/System-Navbar.
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`ds-modal ${maxWidthClass ?? ""} ${className}`.replace(/\s+/g, " ").trim()}
        style={{
          background: "var(--ds-bg-surface)",
          borderRadius: "var(--ds-r-lg)",
          boxShadow: "var(--ds-sh-xl)",
          border: "1px solid var(--ds-border)",
          width: "100%",
          // Tailwind-Klasse (maxWidthClass) gewinnt → dann kein Inline-maxWidth setzen.
          maxWidth: maxWidthClass ? undefined : (maxWidth ?? MODAL_SIZE_MAP[size]),
          maxHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};
